import type { HeatSignal, SignalKind } from './types';
import { getSupabase } from '@/lib/supabase';

/**
 * Signal weights — tuned heuristically.
 * MOCK: real weighting will live server-side post-MVP.
 * Do not attach user-visible meaning to these numbers.
 */
const WEIGHTS: Record<SignalKind, number> = {
  profile_view: 1,
  profile_save: 4,
  message_start: 6,
  feed_dwell: 0.5,
  return_session: 3,
  filter_select: 2,
  filter_apply: 2,
  surface_enter: 0.25,
  // HOTTR.tv signals
  title_view: 1,
  trailer_play: 2,
  episode_start: 3,
  episode_complete: 6,
  creator_follow: 4,
  library_add: 3,
  room_unlock_click: 5,
  rail_dwell: 0.5,
};

let buffer: HeatSignal[] = [];

const now = (): number => Date.now();

const id = (): string =>
  `${now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

/**
 * Per-tab session id for grouping events on the server side. Survives the
 * page lifetime; cleared when the tab closes. We use sessionStorage rather
 * than a singleton so a refresh keeps the same session id.
 */
const SESSION_KEY = 'hottr.session.id';
const sessionId = (): string => {
  if (typeof window === 'undefined') return 'ssr';
  try {
    const existing = window.sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const fresh = `s_${id()}`;
    window.sessionStorage.setItem(SESSION_KEY, fresh);
    return fresh;
  } catch {
    return 'no-storage';
  }
};

/**
 * High-volume signals we DON'T mirror to the server-side `hottr_events`
 * table. Keeps storage cheap; HEAT still buffers them locally for the
 * scoring engine.
 */
const SKIP_SERVER_MIRROR: ReadonlySet<SignalKind> = new Set([
  'feed_dwell',
  'rail_dwell',
  'surface_enter',
]);

const sinkEvent = (signal: HeatSignal) => {
  if (SKIP_SERVER_MIRROR.has(signal.kind)) return;
  const supabase = getSupabase();
  if (!supabase) return;
  // Fire-and-forget. We never block UI on analytics, and we never throw
  // — a dropped event is fine, a thrown render is not.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const db = supabase as any;
  void db
    .from('hottr_events')
    .insert({
      event: signal.kind,
      surface: signal.surface,
      session_id: sessionId(),
      props: {
        subject_id: signal.subjectId ?? null,
        weight: signal.weight,
        ...(signal.meta ?? {}),
      },
    })
    .then(() => undefined);
};

/**
 * Capture a signal. Buffered in-memory for the HEAT scoring engine and
 * (selectively) mirrored to `hottr_events` for server-side product
 * analytics. Local HEAT remains the source of truth for ranking; the
 * server table is for retention/funnel dashboards.
 */
export const capture = (
  kind: SignalKind,
  surface: string,
  subjectId?: string,
  meta?: HeatSignal['meta'],
): HeatSignal => {
  const signal: HeatSignal = {
    id: id(),
    kind,
    subjectId,
    surface,
    weight: WEIGHTS[kind],
    at: now(),
    meta,
  };
  buffer.push(signal);
  sinkEvent(signal);
  return signal;
};

export const peek = (): readonly HeatSignal[] => buffer;

export const drain = (): HeatSignal[] => {
  const out = buffer;
  buffer = [];
  return out;
};
